import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';
import ArticleCard from '@/components/base/ArticleCard';
import SectionTitle from '@/components/base/SectionTitle';
import Pagination from '@/components/base/Pagination';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getHotArticles } from '@/mocks/articles';

const ARTICLES_PER_PAGE = 6;

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [currentPage, setCurrentPage] = useState(1);

  const category = useMemo(() => categories.find((c) => c.slug === slug), [slug]);
  const allArticles = useMemo(() => getArticlesByCategory(slug || ''), [slug]);
  const hotArticles = useMemo(() => getHotArticles(5), []);

  const totalPages = Math.max(1, Math.ceil(allArticles.length / ARTICLES_PER_PAGE));
  const pagedArticles = useMemo(
    () => allArticles.slice((currentPage - 1) * ARTICLES_PER_PAGE, currentPage * ARTICLES_PER_PAGE),
    [allArticles, currentPage]
  );

  if (!category) {
    return (
      <div className="pt-20 pb-20 text-center">
        <p className="text-foreground-500">分类不存在</p>
      </div>
    );
  }

  return (
    <div className="pt-20 pb-14">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="mb-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />
        </div>

        <div className="mb-8">
          <div className="flex items-center gap-4">
            <span className="w-14 h-14 flex items-center justify-center rounded-2xl bg-primary-100 text-primary-500 text-2xl">
              <i className={category.icon}></i>
            </span>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                {category.name}
              </h1>
              <p className="text-sm text-foreground-500 mt-1">
                共 {allArticles.length} 篇文章
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
              {pagedArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>

            {allArticles.length === 0 && (
              <div className="text-center py-20">
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            )}

            {totalPages > 1 && (
              <div className="mt-10">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </div>

          <aside>
            <div className="sticky top-24">
              <SectionTitle title="热门文章" subtitle="大家都在看" />
              <div className="bg-background-100 rounded-xl p-4 border border-background-200/70 space-y-1">
                {hotArticles.map((article) => (
                  <ArticleCard
                    key={article.id}
                    article={article}
                    variant="compact"
                  />
                ))}
              </div>

              <div className="mt-8">
                <SectionTitle title="全部分类" />
                <div className="space-y-1">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.slug}`}
                      className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-colors ${
                        cat.slug === slug
                          ? 'bg-primary-100 text-primary-700 font-medium'
                          : 'text-foreground-600 hover:bg-background-100'
                      }`}
                    >
                      <i className={cat.icon}></i>
                      <span className="flex-1">{cat.name}</span>
                      <span className="text-xs text-foreground-400">{cat.articleCount}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}